import './bridge-war-components.scss';
import { BoardStatKeys, Call, Goal, SeatData, Suit, goalLabels, goalText, goals } from '../../app/types';
import { IconComponent } from '../icon-component/icon-component';
import { SharkBidComponent } from '../shark-bid-component/shark-bid-component';
import { bridgeWarAssets } from '../../assets/bridgewars';
import { classNames } from '../../utils/mixed';
import { convertStringToBid, suitArray } from '../../app-interfaces/shark-game-engine-helper';
import { outputActions } from '../../slices/outputSlice';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { useTranslation } from 'react-i18next';
import React, { FC, useState } from 'react';

const logoMap: Record<string, string> = {
    bwar_7_: bridgeWarAssets.logos.bridgeWar,
    bwar_7_e_: bridgeWarAssets.logos.bridgeWar,
    bwar_8_: bridgeWarAssets.logos.bridgeWarHeroes,
    bwar_8_e_: bridgeWarAssets.logos.bridgeWarHeroes,
    bwar_c_: bridgeWarAssets.logos.bridgeWarChallenger,
    bwar_dd_: bridgeWarAssets.logos.bridgeWarChallenger,
    bwar_ex_: bridgeWarAssets.logos.bridgeWarExtreme,
};

export const BridgeWarLogoComponent: FC = () => {
    const { urlParams } = useAppSelector((state) => state.app);
    const logoPrefix = urlParams?.['mode'] ?? 'bwar_7_';
    const logo = logoMap[logoPrefix] ?? logoMap['bwar_7_'];
    return (
        <div className="BridgeWarLogoComponent">
            <img src={logo} alt="BridgeWar Logo" />
        </div>
    );
};

export const BridgeWarInfosComponent: FC = () => {
    const { t } = useTranslation();
    return (
        <div className="BridgeWarInfosComponent">
            <IconComponent icon="attacker" />
            <div>{t('bridgeWar.info.attacker')}</div>
            <IconComponent icon="defender" />
            <div>{t('bridgeWar.info.defender')}</div>
            <IconComponent icon="compass" />
            <div>{t('bridgeWar.info.compass')}</div>
        </div>
    );
};

export const BridgeWarGameInfoComponent: FC<{ seatData: SeatData }> = ({ seatData }) => {
    const { bridgeWarGameInfo } = useAppSelector((state) => state.table);

    const avatar = seatData?.bridgeWarAvatar ? bridgeWarAssets.avatars[seatData.bridgeWarAvatar] : undefined;

    if (bridgeWarGameInfo == null) {
        return;
    }
    return (
        <div className="BridgeWarGameInfoComponent" style={{ background: avatar?.color ?? 'transparent' }}>
            {bridgeWarGameInfo.map((info) => (
                <div className={`line`} key={info}>
                    {info}
                </div>
            ))}
        </div>
    );
};

export const BridgeWarGameSelectorComponent: FC = () => {
    const dispatch = useAppDispatch();
    const { output_setGameSelector } = outputActions;
    const { bridgeWarGameSelector } = useAppSelector((state) => state.table);
    const {
        sharkLayout: { selectedCardSet },
    } = useAppSelector((state) => state.app);
    const [step, setStep] = useState(0);
    const [trump, setTrump] = useState<Call>();
    const [goal, setGoal] = useState<Goal>();
    const [done, setDone] = useState<boolean>();
    const { title, text, buttonLabelBack, buttonLabelNext } = bridgeWarGameSelector[step];

    function handleBack() {
        setStep((_step) => {
            return _step - 1;
        });
    }

    function handleNext() {
        if (step < bridgeWarGameSelector.length - 1) {
            setStep((_step) => {
                return _step + 1;
            });
        } else {
            dispatch(output_setGameSelector({ goal, trump, done: true }));
            setDone(true);
        }
    }

    function handleTrump(trump: Call) {
        setTrump(trump);
        dispatch(output_setGameSelector({ trump, done: false }));
    }

    const nextDisabled = (step === 1 && !trump) || (step === 2 && !goal);

    if (done) {
        return;
    }

    return (
        <section className="BridgeWarGameSelectorComponent">
            {title && <div className="title" dangerouslySetInnerHTML={{ __html: title }} />}
            {text && <div className="text" dangerouslySetInnerHTML={{ __html: text }} />}
            {step === 1 && (
                <div className="trumps">
                    {['1c', '1d', '1h', '1s', '1nt'].map((_call) => {
                        const call = _call as Call;
                        const bid = convertStringToBid(call);
                        return (
                            <div
                                key={_call}
                                onClick={() => handleTrump(call)}
                                className={classNames('trump', trump === call && 'selected')}
                            >
                                <SharkBidComponent bid={bid} selectedCardSet={selectedCardSet} />
                            </div>
                        );
                    })}
                </div>
            )}
            {step === 2 && goal && (
                <div className="goal-message">
                    {goal === 7 && <span>{goalText[0]}</span>}
                    {goal === 9 && <span>{goalText[1]}</span>}
                    {goal === 12 && <span>{goalText[2]}</span>}
                </div>
            )}
            {step === 2 && (
                <div className="goals">
                    {goals.map((_goal, index) => (
                        <button
                            key={_goal}
                            onClick={() => setGoal(_goal)}
                            className={classNames('goal', goal === _goal && 'selected')}
                        >
                            {goalLabels[index]}
                        </button>
                    ))}
                </div>
            )}

            <div className="buttons">
                {buttonLabelBack && (
                    <button className="buttonLabelBack" onClick={handleBack}>
                        {buttonLabelBack}
                    </button>
                )}
                {buttonLabelNext && (
                    <button className="buttonLabelNext" onClick={handleNext} disabled={nextDisabled}>
                        {buttonLabelNext}
                    </button>
                )}
            </div>
        </section>
    );
};

export const suits = {
    C: 'clubs',
    S: 'spades',
    H: 'hearts',
    D: 'diamonds',
};

export const contractSuits: Record<string, string> = {
    '0': Suit.clubs,
    '1': Suit.diamonds,
    '2': Suit.hearts,
    '3': Suit.spades,
    '4': 'NT',
};

export const BridgeWarBoardStatsComponent: React.FC = () => {
    const { boardStats, myBoardStatKeys } = useAppSelector((state) => state.table);
    const { t } = useTranslation();

    const d = [
        t('boardStats.bridgePosition.n'),
        t('boardStats.bridgePosition.e'),
        t('boardStats.bridgePosition.s'),
        t('boardStats.bridgePosition.w'),
    ];

    const columnTitles = [
        ['contract', t('boardStats.contract')],
        ['lead', 'Lead'],
        ['tricks', 'Tricks'],
        ['score', t('boardStats.score')],
        ['result', t('boardStats.result')],
        ['players', '# of Players'],
    ];

    if (!boardStats) {
        return null;
    }

    return (
        <section className={['BridgeWarBoardStatsComponent', boardStats.length === 0 ? 'NoStats' : ''].join(' ')}>
            <div className="table">
                <div>
                    {columnTitles.map(([key, title]) => (
                        <div key={title} className={key}>
                            {title}
                        </div>
                    ))}
                </div>
                {boardStats.map((_boardStats, index) => {
                    const keyStats = (Object.keys(_boardStats.key) as (keyof BoardStatKeys)[])
                        .map((key) => _boardStats.key[key])
                        .sort()
                        .join('-');
                    const myStats = myBoardStatKeys
                                    ? (Object.keys(myBoardStatKeys) as (keyof BoardStatKeys)[])
                                        .map((key) => myBoardStatKeys[key])
                                        .sort()
                                        .join('-')
                                    : '';
                    return (
                        <div key={`_boardStats-${index}`} className={keyStats === myStats ? 'isMe' : ''}>
                            <div className="contract">
                                <div
                                    className={
                                        _boardStats.key.c >= 10
                                        ? contractSuits[`${_boardStats.key.c}`.substring(1, 2)]
                                        : 'passOut'
                                    }
                                >
                                    {`${_boardStats.key.c}`.substring(1, 2) === '4' && (
                                        <div className="notrump">
                                            {contractSuits[`${_boardStats.key.c}`.substring(1, 2)]}
                                        </div>
                                    )}
                                    <div className="level">
                                        {_boardStats.key.c > 0 ? `${_boardStats.key.c}`.substring(0, 1) : 'Pass out'}
                                    </div>
                                    <div className="x">&nbsp;{' XX'.substring(0, _boardStats.key.x)}</div>
                                </div>
                            </div>
                            <div className="lead">
                                <div
                                    className={
                                        _boardStats.key.l >= 0
                                        ? suits[suitArray[_boardStats.key.l].substring(0, 1) as keyof typeof suits]
                                        : ''
                                    }
                                >
                                    <span>
                                        {_boardStats.key.l >= 0 ? suitArray[_boardStats.key.l].substring(1) : '-'}
                                    </span>
                                </div>
                            </div>
                            <div className="tricks">
                                {_boardStats.key.r}
                            </div>
                            <div className="score">{_boardStats.key.s}</div>
                            <div className="result">
                                {Math.round((_boardStats.result + Number.EPSILON) * 100) / 100}
                            </div>
                            <div className="players">{_boardStats.count}</div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
};